<script lang="ts">
	import Image from '../components/image.svelte';
	import SbRichtext from '../components/sbRichtext.svelte';
	import PageGrid from '../components/pageGrid.svelte';
	import Typeform from '../parts/typeform/typeform.svelte';
	import { trackLeadGenerated } from '$lib/segment/client';
	import DivWrapper from '../components/divWrapper.svelte';
	import { identifySubmission } from '$lib/utils/typeform';
	import Button from '$lib/buttons/v3/button.svelte';
	import type { SbImage } from '../types';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';

	interface Props {
		content: ISbStoryData<{
			asset: SbImage;
			title: ISbRichtext;
			subtitle: ISbRichtext;
			hide_welcome_screen: boolean;
			cta_label: string;
			typeform_id: string;
			success_title: ISbRichtext;
			success_subtitle: ISbRichtext;
			success_cta_label: string;
			lead_event_active: boolean;
			lead_event_value: string;
			lead_event_content_name: string;
			lead_event_content_category: string;
			full_width: boolean;
			lazy_load: boolean;
		}>['content'];
	}

	let { content }: Props = $props();

	let status = $state<'start' | 'form' | 'success'>(content.hide_welcome_screen ? 'form' : 'start');

	async function handleSubmit({ formId, responseId }: { formId: string; responseId: string }) {
		status = 'success';

		if (content.lead_event_active) {
			await trackLeadGenerated(
				content.lead_event_content_category,
				content.lead_event_content_name,
				content.lead_event_value
			);
		}

		await identifySubmission(formId, responseId);
	}

	let GridComponent = $derived(content.full_width ? DivWrapper : PageGrid);
</script>

<GridComponent class={content.full_width ? 'h-full w-full' : ''}>
	<section
		use:editable={content}
		class="relative col-span-full h-full overflow-hidden"
		class:rounded-3xl={!content.full_width}
	>
		{#if content.asset?.filename}
			<div class="absolute inset-0 z-10 h-full w-full max-lg:hidden">
				<Image class="h-full w-full object-cover" src={content.asset.filename} />
			</div>
		{/if}
		<div
			class="relative z-20 flex h-full w-full items-center justify-center"
			class:lg:p-4={!content.full_width}
		>
			{#if status === 'start'}
				<div
					class="max-w-[525px] rounded-xl bg-stone-4 px-4 py-6 text-center lg:px-[62px] lg:py-12"
				>
					<div class="text-brown-1">
						<div class="text-balance font-serif text-[32px]/[130%] tracking-[-0.64px]">
							<SbRichtext text={content.title} />
						</div>
						<div class="mt-3 text-balance text-[20px]/[140%]">
							<SbRichtext text={content.subtitle} />
						</div>
					</div>
					<div class="mt-7">
						<Button onclick={() => (status = 'form')} class="btn-large">{content.cta_label}</Button>
					</div>
				</div>
			{:else if status === 'form' || content.hide_welcome_screen}
				<Typeform
					formId={content.typeform_id}
					onsubmit={handleSubmit}
					lazy={content.lazy_load}
					rounded={!content.full_width}
				/>
			{:else if status === 'success'}
				<div
					class="max-w-[525px] rounded-xl bg-stone-4 px-4 py-6 text-center lg:px-[62px] lg:py-12"
				>
					<div class="text-brown-1">
						<div class="text-balance font-serif text-[32px]/[130%] tracking-[-0.64px]">
							<SbRichtext text={content.success_title} />
						</div>
						<div class="mt-3 text-balance text-[20px]/[140%]">
							<SbRichtext text={content.success_subtitle} />
						</div>
					</div>
					<div class="mt-7 flex justify-center">
						<Button href="/food-recycler" class="btn-large">{content.success_cta_label}</Button>
					</div>
				</div>
			{/if}
		</div>
	</section>
</GridComponent>
